import { useEffect, useMemo, useRef, useState } from "react";
import { Search, Hash, X, User } from "lucide-react";

function fullName(e) {
  return [e?.basic?.first_name, e?.basic?.middle_name, e?.basic?.last_name].filter(Boolean).join(" ");
}

/**
 * Searchable employee picker — type name/code, or enter employee code exactly.
 * Avoids huge <select> lists when the directory is large.
 */
export default function EmployeeLookup({
  employees = [],
  value = "",
  onChange,
  placeholder = "Search by name or employee code…",
  testid = "employee-lookup",
  disabled = false,
}) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [codeInput, setCodeInput] = useState("");
  const [err, setErr] = useState("");
  const wrapRef = useRef(null);

  const selected = useMemo(
    () => employees.find((e) => e.id === value) || null,
    [employees, value]
  );

  const matches = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (s.length < 1) return [];
    return employees
      .filter((e) => {
        const name = fullName(e).toLowerCase();
        const code = String(e?.basic?.code || "").toLowerCase();
        const email = String(e?.contact?.official_email || e?.contact?.email || "").toLowerCase();
        return name.includes(s) || code.includes(s) || email.includes(s);
      })
      .slice(0, 15);
  }, [employees, q]);

  useEffect(() => {
    const onDoc = (ev) => {
      if (wrapRef.current && !wrapRef.current.contains(ev.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (emp) => {
    onChange?.(emp.id);
    setQ("");
    setCodeInput("");
    setErr("");
    setOpen(false);
  };

  const clear = () => {
    onChange?.("");
    setQ("");
    setCodeInput("");
    setErr("");
  };

  const resolveCode = () => {
    const code = codeInput.trim().toLowerCase();
    if (!code) {
      setErr("Enter an employee code");
      return;
    }
    const found = employees.find((e) => String(e?.basic?.code || "").toLowerCase() === code);
    if (!found) {
      setErr(`No employee found for code “${codeInput.trim()}”`);
      return;
    }
    pick(found);
  };

  return (
    <div className="space-y-2" ref={wrapRef} data-testid={testid}>
      {selected ? (
        <div
          className="flex items-start justify-between gap-3 rounded border border-slate-200 bg-slate-50 px-3 py-2.5"
          data-testid={`${testid}-selected`}
        >
          <div className="flex items-start gap-2.5 min-w-0">
            <div className="h-9 w-9 rounded-full bg-[#226DB4] text-white flex items-center justify-center shrink-0">
              <User className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-slate-800 truncate">{fullName(selected)}</div>
              <div className="text-xs text-slate-500 truncate">
                {selected.basic?.code}
                {selected.employment?.status ? ` · ${selected.employment.status}` : ""}
              </div>
            </div>
          </div>
          {!disabled && (
            <button
              type="button"
              onClick={clear}
              className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-white rounded"
              title="Clear"
              data-testid={`${testid}-clear`}
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              disabled={disabled}
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setOpen(true);
                setErr("");
              }}
              onFocus={() => setOpen(true)}
              placeholder={placeholder}
              className="w-full border border-slate-200 rounded pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#226DB4]/30 focus:border-[#226DB4] bg-white disabled:bg-slate-50"
              data-testid={`${testid}-search`}
              autoComplete="off"
            />
            {open && q.trim() && (
              <ul
                className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto bg-white border border-slate-200 rounded shadow-lg"
                data-testid={`${testid}-results`}
              >
                {matches.length === 0 ? (
                  <li className="px-3 py-2.5 text-sm text-slate-400">No matches</li>
                ) : (
                  matches.map((e) => (
                    <li key={e.id}>
                      <button
                        type="button"
                        onClick={() => pick(e)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-[#226DB4]/5 border-b border-slate-50 last:border-0"
                        data-testid={`${testid}-option-${e.basic?.code}`}
                      >
                        <span className="font-medium text-slate-800">{fullName(e)}</span>
                        <span className="text-slate-500"> · {e.basic?.code}</span>
                        {e.employment?.status ? (
                          <span className="text-slate-400"> · {e.employment.status}</span>
                        ) : null}
                      </button>
                    </li>
                  ))
                )}
              </ul>
            )}
          </div>

          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Hash className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <input
                type="text"
                disabled={disabled}
                value={codeInput}
                onChange={(e) => {
                  setCodeInput(e.target.value);
                  setErr("");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    resolveCode();
                  }
                }}
                placeholder="Or type exact employee code (e.g. EMP001)"
                className="w-full border border-slate-200 rounded pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#226DB4]/30 focus:border-[#226DB4] bg-white disabled:bg-slate-50"
                data-testid={`${testid}-code`}
                autoComplete="off"
              />
            </div>
            <button
              type="button"
              disabled={disabled}
              onClick={resolveCode}
              className="shrink-0 px-3 py-2 text-sm font-medium border border-slate-200 rounded bg-white hover:bg-slate-50 text-slate-700 disabled:opacity-60"
              data-testid={`${testid}-find-code`}
            >
              Find
            </button>
          </div>
        </>
      )}

      {err ? (
        <div className="text-xs text-rose-600" data-testid={`${testid}-error`}>
          {err}
        </div>
      ) : null}
    </div>
  );
}
